
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl, isNull } from '@core/tools';
import { IAppletSignupDetail, IApproveItem, IApproveManageParam, IApproveTaskDetail, IApproveTaskParam, IApproveTaskParams, IApproveTaskRes, IApproveUserItem, IUpdateApproveParam } from '@core/main/types/system/approval';
import { IBusinessLineItem } from '@core/main/types';

const hostUrl = getHostbaseUrl();

/**
 * 基础配置 - 审批人配置列表
 */
export async function queryApproveUserList() {
  let data = [] as IApproveUserItem[];

  try {
    const { code, context, message } = await Http.get<IApproveUserItem[]>(`${hostUrl}/iam/approve/user/list`);

    if (code === EAxiosResponseCode.Succeed) {
      const items = context || [] as IApproveUserItem[];

      data = items || [] as IApproveUserItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 基础配置 - 审批人配置 - 修改
 */
export async function updateApproveUser(params: IUpdateApproveParam) {
  params = JSON.parse(JSON.stringify(params)) as IUpdateApproveParam;

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/iam/approve/user/config`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 获取审批任务列表
 * @param params - 查询参数
 */
export async function queryApproveTaskList(params: IApproveManageParam) {
  let data = [] as IApproveTaskRes[];
  let total = 0;

  try {
    const { code, context, message } = await Http.get<{ data: IApproveTaskRes[]; total: number }>(`${hostUrl}/iam/approve/task/page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IApproveTaskRes[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 我发起的审批分页
 * @param params - 查询参数
 */
export async function queryInitiateApprovList(params: IApproveManageParam) {
  let data = [] as IApproveTaskRes[];
  let total = 0;

  try {
    const { code, context, message } = await Http.get<{ data: IApproveTaskRes[]; total: number }>(`${hostUrl}/iam/approve/my-task/page`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IApproveTaskRes[]; total: number };

      data = items || [];
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 基础配置 - 审批任务 - 通过、驳回
 */
export async function updateApproveTask(params: IApproveTaskParam) {
  params = JSON.parse(JSON.stringify(params)) as IApproveTaskParam;

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/iam/approve/task/confirm`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 基础配置 - 审批任务 - 批量通过、驳回
 */
export async function updateApproveTaskBatch(params: IApproveTaskParam) {
  params = JSON.parse(JSON.stringify(params)) as IApproveTaskParam;

  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/iam/approve/task/confirm-batch`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 基础配置 - 审批任务 - 详情
 */
export async function queryApproveTaskDetail(params: IApproveTaskParams) {
  let data = {} as IApproveTaskDetail;

  try {
    const { code, context, message } = await Http.get<any>(`${hostUrl}/iam/approve/task/detail`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const items = context || {} as IApproveTaskDetail;

      data = items || {} as IApproveTaskDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 审核管理 - 小程序报名详情
 */
export async function queryAppletSignupDetail(id: number) {
  let data = {} as IAppletSignupDetail;

  try {
    const { code, context, message } = await Http.get<any>(`${hostUrl}/side/duty/duty-info/signup-detail`, { id });

    if (code === EAxiosResponseCode.Succeed) {
      const items = context || {} as IAppletSignupDetail;

      data = items || {} as IAppletSignupDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 审核管理 - 判断用户是否有给定类型任务的审批权限
 */
export async function queryApprovePermission(type: number, step: number) {
  let enabled = true;

  try {
    const { code, context } = await Http.post<any>(
      `${hostUrl}/iam/approve/permission`,
      {
        type,
        step, 
      },
    );
  
    if (code !== EAxiosResponseCode.Succeed) {
      enabled = false;
    } else if (isNull(context) || context.length === 0) {
      enabled = false;
    } else {
      enabled = context;
    }
  } catch (error) {
    console.log(error);
    
    enabled = false;
  }
  
  return enabled;

}

/**
 * 获取当前端审批类型
 */
export async function queryApproveList() {
  let data = [] as IApproveItem[];

  try {
    const { code, context, message } = await Http.get<IApproveItem[]>(`${hostUrl}/iam/approve/type/list`);

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as IApproveItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/** 查询业务线列表 */
export async function queryApproveLine() {
  let data = [] as IBusinessLineItem[];

  try {
    const { code, context, message } = await Http.get<IBusinessLineItem[]>(`${hostUrl}/iam/approve/business/list`);

    if (code === EAxiosResponseCode.Succeed) {
      let res = context || [];

      data = res || [] as IBusinessLineItem[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}